/**
 * Interactive demo script — the "kanji investigation" tour.
 *
 * Iter-41 narrative: Kanji Build is positioned as an investigation tool,
 * not a teaching tool. The demo walks through a real investigative
 * question — "I just saw 詩 (poetry). What is this kanji actually made
 * of, and what does it have to do with the other kanji I already know?"
 *
 * Each beat:
 *   - setup?(ctx)       — async action that puts the app into the right
 *                         state (navigate, click a tab) before the
 *                         instruction is shown.
 *   - target            — data-testid of the element the user interacts
 *                         with. Drives spotlight + tooltip placement.
 *   - prompt            — instruction shown until the user acts.
 *   - await             — how to detect they did it:
 *       { kind: 'input-contains', testId, value }
 *       { kind: 'click', testId | prefix }
 *       { kind: 'route-includes', substr }
 *       { kind: 'hover-part', char }
 *       { kind: 'ack' }                              — needs the user
 *         to press the "Got it" button on the tooltip. Used for beats
 *         where there's nothing to click but a teaching message to
 *         absorb. Replaces all auto-advance — the user is always in
 *         control of pacing now.
 *   - affirm            — praise / teaching message after they act.
 *   - affirmTag?        — small uppercase tag word (default "Nice").
 *   - placement?        — tooltip placement hint, helps the narrator
 *                         avoid overlapping with dropdowns / dynamic
 *                         content. One of:
 *           'auto'              — default (prefer below, fall back above)
 *           'above'             — force above
 *           'below'             — force below
 *           'left'              — force to the left of the target
 *           'right'             — force to the right
 *           'avoid-dropdown'    — for inputs that open an autocomplete:
 *                                 placed to the right or far below
 *                                 the expected dropdown footprint.
 *           'side'              — alias for right (LTR) — same idea.
 */

const DEMO_KANJI = ["時", "寺", "詩", "持", "侍", "海", "水", "氵", "青", "清", "晴", "情"];
const DEMO_WORDS = ["時計"];
export const DEMO_PRELOAD = { kanji: DEMO_KANJI, words: DEMO_WORDS };

export const SCRIPT = [
  // ── 1. Cold open ────────────────────────────────────────────────────────
  {
    target: null,
    placement: "center",
    prompt:
      "Welcome to Kanji Build. This tool is designed to complement your kanji learning with deeper understanding.",
    await: { kind: "ack" },
    affirm: "",
    affirmTag: "",
  },

  // ── 2. Search by meaning ───────────────────────────────────────────────
  {
    setup: async ({ navigate }) => navigate("/"),
    target: "search-input",
    placement: "avoid-dropdown",
    prompt:
      "We'll start by searching for the kanji for time. Type 'time' into the search box at the top.",
    await: { kind: "input-contains", testId: "search-input", value: "time" },
    affirm: "",
    affirmTag: "",
  },

  // ── 3. Open 時 ─────────────────────────────────────────────────────────
  {
    target: "search-result-時",
    placement: "right",
    prompt: "Now open 時 from the results below.",
    await: { kind: "route-includes", substr: "/kanji/" },
    affirm: "",
    affirmTag: "",
  },

  // ── 4a. The card holds the facts ───────────────────────────────────────
  {
    target: "focus-card-info",
    placement: "below",
    prompt:
      "This is 時's card. Meaning, on'yomi and kun'yomi readings, stroke count, usage frequency and every word it appears in live here — the home for any kanji's facts.",
    await: { kind: "ack" },
    affirm: "",
    affirmTag: "",
  },

  // ── 4b. The constituents ───────────────────────────────────────────────
  {
    target: "focus-built-from",
    placement: "above",
    prompt:
      "Now look at the constituents: 時 is built from 日 (sun) and 寺 (temple). That fact is the start of the investigation.",
    await: { kind: "ack" },
    affirm: "",
    affirmTag: "",
  },

  // ── 5. Open the Build tab ──────────────────────────────────────────────
  {
    target: "tab-build",
    placement: "below",
    prompt: "Open the BUILD tab to see how 時 is actually constructed.",
    await: { kind: "click", testId: "tab-build" },
    affirm: "",
    affirmTag: "",
  },

  // ── 6a. The animation ──────────────────────────────────────────────────
  {
    target: "build-glyph-frame",
    placement: "right",
    prompt:
      "Each stroke of 時 is colour-tinted to match the part it belongs to. Click the ▶ arrow on the glyph to watch it draw stroke by stroke before you continue.",
    await: { kind: "ack" },
    affirm: "",
    affirmTag: "",
  },

  // ── 6b. The deeper constituent tree ────────────────────────────────────
  {
    target: "build-depth-rows",
    placement: "right",
    prompt:
      "Below the glyph is the full constituent tree — not just 時's immediate parts (日, 寺) but every layer beneath. Click any part to walk further down. You can trace any kanji back to its primitives.",
    await: { kind: "ack" },
    affirm: "",
    affirmTag: "",
  },

  // ── 6c. The compound words 時 appears in ───────────────────────────────
  {
    target: "focus-used-in",
    placement: "below",
    prompt:
      "Below the tree, every JLPT compound word that uses 時 is listed: 時計 (watch), 時間 (time/hour), 時々 (occasionally), 当時 (at that time). Tap a card to open the word. Kanji you know are unhighlighted, new ones get a marker — so you can see at a glance which compounds you can already read.",
    await: { kind: "ack" },
    affirm: "",
    affirmTag: "",
  },

  // ── 6d. Reverse view: 寺's grows-into list ────────────────────────────
  {
    setup: async ({ navigate }) => navigate("/kanji/%E5%AF%BA?tab=build"),
    target: "focus-grows-into",
    placement: "above",
    prompt:
      "And the reverse view. We've jumped to 寺's card. Its 'grows into' list shows every kanji built FROM 寺 — 時 (where we came from), 詩 (poetry), 持 (hold), 侍 (samurai), 待 (wait), 等 (equal), 特 (special). The investigation flows both ways.",
    await: { kind: "ack" },
    affirm: "",
    affirmTag: "",
  },

  // ── 7. Switch to Phonetic view ─────────────────────────────────────────
  {
    target: "build-view-phonetic",
    placement: "below",
    prompt: "Now switch the Build view to PHONETIC. Same kanji, a different lens.",
    await: { kind: "click", testId: "build-view-phonetic" },
    affirm: "",
    affirmTag: "",
  },

  // ── 8. Acknowledge the family ──────────────────────────────────────────
  {
    target: "phonetic-tree-container",
    placement: "right",
    prompt:
      "Here's the same family from a phonetic angle. 寺 isn't just a visual building block — it's a sound clue. Many kanji built from 寺 read じ or し. Look at the family: 侍 (samurai), 持 (hold), 詩 (poetry). Memorise one piece, unlock four kanji.",
    await: { kind: "ack" },
    affirm: "",
    affirmTag: "",
  },

  // ── 9. Voicing detection: visit 詩 ─────────────────────────────────────
  {
    setup: async ({ navigate }) => navigate("/kanji/%E8%A9%A9?tab=build&view=phonetic"),
    target: "phonetic-tree-container",
    placement: "right",
    prompt:
      "Here's 詩 (poetry). It reads し, not じ. Different sound — same family. Kanji Build still flags 寺 as the driver because し and じ are the same canonical reading, one voiced, one not. Real linguistics, not naive string matching.",
    await: { kind: "ack" },
    affirm: "",
    affirmTag: "",
  },

  // ── 10. Browse mode — phonetic atlas ───────────────────────────────────
  {
    setup: async ({ navigate }) => {
      navigate("/");
      // After navigate, <ScrollToTopOnNav> fires window.scrollTo(0,0) on
      // the route change. We have to wait for THAT to land before we
      // scroll our target into view, otherwise the route-change scroll
      // overwrites us and the user lands on the home page header,
      // unable to see the BROWSE strip the next prompt is pointing at.
      //
      // We also do a second scrollIntoView a beat later, defensively,
      // in case sticky layout shifts move the target after first paint.
      const scrollWhenReady = async () => {
        for (let i = 0; i < 12; i++) {
          await new Promise((r) => setTimeout(r, 100));
          const el = document.querySelector('[data-testid="path-mode-phonetic"]');
          if (el) {
            el.scrollIntoView({ behavior: "auto", block: "center" });
            return true;
          }
        }
        return false;
      };
      await scrollWhenReady();
      // Defensive second pass after layout settles.
      setTimeout(() => {
        const el = document.querySelector('[data-testid="path-mode-phonetic"]');
        if (el) el.scrollIntoView({ behavior: "auto", block: "center" });
      }, 400);
    },
    target: "path-mode-phonetic",
    placement: "below",
    prompt: "Now zoom out. Click PHONETIC in the BROWSE strip.",
    await: { kind: "click", testId: "path-mode-phonetic" },
    affirm: "",
    affirmTag: "",
  },

  // ── 11. Filter the atlas ───────────────────────────────────────────────
  {
    target: "atlas-filter",
    placement: "avoid-dropdown",
    prompt: "Type 'blue' into the atlas filter. We'll find the family for 青.",
    await: { kind: "input-contains", testId: "atlas-filter", value: "blue" },
    affirm:
      "There — 青 (blue) drives readings せい, しょう and じょう across seven kanji: 情 (feeling), 晴 (clear), 清 (pure), 精 (refined), 請 (request), 靖 (peaceful) and 静 (quiet). 青 itself sits one layer above as the common source. Every blue thing in the language traces back here.",
    affirmTag: "One driver, many kanji",
  },

  // ── 12. Browse by radical ──────────────────────────────────────────────
  {
    setup: async ({ navigate }) => navigate("/"),
    target: "path-mode-radicals",
    placement: "below",
    prompt: "One more lens. Click RADICALS in the BROWSE strip.",
    await: { kind: "click", testId: "path-mode-radicals" },
    affirm: "",
    affirmTag: "",
  },

  // ── 13. Variant radical → parent kanji ─────────────────────────────────
  {
    target: "radical-row-氵",
    placement: "above",
    prompt: "Find the 氵 row — it's the side-form of 水 (water). Click the 氵 glyph on the left of the row.",
    await: { kind: "click", testId: "radical-link-氵" },
    affirm:
      "氵 isn't its own kanji — it's water in disguise. Kanji Build sends you to 水's card, and 水's 'grows into' list shows every kanji built from 氵 — 海, 河, 池, 流, 江, 油, 波, 清. The investigation is connected end to end.",
    affirmTag: "Connected",
  },

  // ── 14. Mark two kanji as known → seed the learning loop ───────────────
  {
    setup: async () => {
      // Quietly mark 日 + 寺 as known on behalf of the demo user.
      // The journal will then surface 時 as ready-to-learn (every part known)
      // alongside the multi-kanji words 時計, 時時, 時々.
      try {
        const { setProgress: sp } = await import("@/lib/api");
        await sp("日", "known");
        await sp("寺", "known");
      } catch (e) {
        // Non-fatal — the demo still teaches the concept even if the
        // mark-as-known calls fail. Surfaced in console for debugging.
        console.warn("demo setProgress(日/寺) failed", e);
      }
    },
    target: null,
    placement: "center",
    prompt:
      "Now the learning side. Kanji Build watches what you mark as 'known'. The moment two known kanji unlock another, that new kanji surfaces as 'ready to learn'. We've just marked 日 (sun) and 寺 (temple) for you.",
    await: { kind: "ack" },
    affirm: "",
    affirmTag: "",
  },

  // ── 15. Visit the Journal — show the unlocked tiles ────────────────────
  {
    setup: async ({ navigate }) => navigate("/journal"),
    target: "journal-section-ready-kanji",
    placement: "above",
    prompt:
      "Your Journal. The 'Ready kanji' section now lists 時 (time) — every component you know — and below it, 'Ready compounds' lists every word that uses only kanji you know (時計, 時間, etc.). The graph does the work; you just follow the threads.",
    await: { kind: "ack" },
    affirm: "",
    affirmTag: "",
  },

  // ── 16. Close + CTA ────────────────────────────────────────────────────
  {
    setup: async ({ navigate }) => navigate("/"),
    target: null,
    placement: "center",
    prompt:
      "That's the tour. Kanji Build is the tool you reach for when you are intrigued about how the kanji you are learning relates to the system as a whole. Learn Kanji organically as you explore.",
    await: { kind: "ack" },
    affirm: "",
    affirmTag: "",
  },
];
